import Image from "next/legacy/image";
import { useDispatch, useSelector } from "react-redux";

import { CurrencyWrapper as Currency, Modal } from "@chef/smart-components";
import { BASE_64_1X1_GREY } from "@chef/constants";
import { getWeek, getYear, nextDeliveryWeekDateObj } from "@chef/helpers";
import { Breakfast, Trash } from "@chef/icons/small";
import {
  IDeviationProduct,
  selectAddonProducts,
  selectCartModal,
  setAddonProducts,
  toggleCartModal,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import {
  getImageFromImages,
  getImageFromVariation,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "@chef/state-management/helpers";
import { Card, Hr } from "@chef/components";

import { intl } from "./StandaloneBasketOverviewModal.Intl";

export const StandaloneBasketOverviewModal = () => {
  const dispatch = useDispatch();

  const { open, week, year } = useSelector(selectCartModal);

  return (
    <Modal
      open={open}
      onClose={() => dispatch(toggleCartModal({ week, year }))}
      interactions={[["top-close-button", "enabled"]]}
    >
      <Card noPadding>
        <MobileView />

        <DesktopView />
      </Card>
    </Modal>
  );
};

const MobileView = () => {
  return (
    <div className="flex flex-col px-4 pt-8 grow lg:hidden">
      <h2>{intl.IN_YOUR_SUBSCRIPTION}</h2>

      <Receipt />
    </div>
  );
};

const DesktopView = () => {
  const addonProducts = useSelector(selectAddonProducts);
  const { allVariations } = useAllVariations();

  return (
    <div className="hidden lg:flex grow">
      <div className="flex flex-col px-6 py-8 grow">
        <h2 className="text-xl">
          <strong>{intl.IN_YOUR_SUBSCRIPTION}</strong>
        </h2>

        <ul className="grid mt-4 lg:grid-cols-3 lg:gap-6">
          {addonProducts.map(({ variationId, quantity }) => {
            const variation = allVariations.find(
              (variation) => variation.variationId === variationId,
            );

            const imgSrc = variation
              ? getImageFromVariation({ variation, size: "MEDIUM" })
              : BASE_64_1X1_GREY;

            return (
              <li key={variationId} className="flex flex-col self-stretch ">
                <Card noPadding className="grow">
                  <Image
                    src={imgSrc}
                    alt={variation?.name}
                    layout="responsive"
                    width={192}
                    height={287}
                  />

                  <p className="p-4">
                    {quantity} x {variation?.name}
                  </p>
                </Card>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="self-stretch px-6 py-8 bg-grey-3 justify-self-end">
        <h2 className="text-xl">
          <strong>{intl.RECEIPT}</strong>
        </h2>

        <Receipt />
      </div>
    </div>
  );
};

const Receipt = () => {
  const dispatch = useDispatch();

  const { allVariations } = useAllVariations();
  const addonProducts = useSelector(selectAddonProducts);

  const { addonSum, addonCount } = addonProducts.reduce(
    ({ addonSum, addonCount }, product) => {
      return {
        addonSum: addonSum + product.price * product.quantity,
        addonCount: addonCount + product.quantity,
      };
    },
    { addonSum: 0, addonCount: 0 },
  );

  const removeProduct = (updatedProduct: IDeviationProduct) => {
    const quantityAfterRemoval = updatedProduct.quantity - 1;

    const productShouldBeRemovedFromBasket = quantityAfterRemoval === 0;
    if (productShouldBeRemovedFromBasket) {
      return dispatch(
        setAddonProducts(
          addonProducts.filter(
            (basketProduct) =>
              basketProduct.productId !== updatedProduct.productId,
          ),
        ),
      );
    }

    return dispatch(
      setAddonProducts(
        addonProducts.map((basketProduct) => {
          if (basketProduct.productId !== updatedProduct.productId) {
            return basketProduct;
          }

          return { ...updatedProduct, quantity: quantityAfterRemoval };
        }),
      ),
    );
  };

  return (
    <>
      <Hr className="mt-6" />

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center">
          <Breakfast className="w-7.5 h-7.5 p-1.5" />
          <p className="ml-2">{intl.FORMAT_ADDON_COUNT(addonCount)}</p>
        </div>

        <div className="ml-2">
          <Currency>{addonSum}</Currency>
        </div>
      </div>

      <ul className="flex flex-col gap-2 mt-5">
        {addonProducts.map((product) => {
          const { quantity, price, variationId } = product;
          const variation = allVariations.find(
            (variation) => variation.variationId === variationId,
          );

          const imgSrc = getImageFromImages({
            images: variation?.images || [],
            size: "THUMBNAIL",
          });

          return (
            <li key={variationId} className="flex items-center justify-between">
              <div className="flex items-center shrink-0">
                <Trash
                  className="w-7.5 h-7.5 p-1.5 cursor-pointer"
                  onClick={() => removeProduct(product)}
                />

                <div className="relative w-12 h-12 ml-3 lg:hidden">
                  <Image
                    src={imgSrc}
                    alt={variation?.name}
                    width={48}
                    height={48}
                    objectFit="cover"
                  />
                </div>

                <p className="inline-block ml-2 shrink w-[180px] lg:w-[266px]">{`${quantity} x ${variation?.name}`}</p>
              </div>

              <div className="ml-2">
                <Currency>{price * quantity}</Currency>
              </div>
            </li>
          );
        })}
      </ul>

      <Hr className="mt-3" />

      <div className="flex justify-between my-3">
        <p>
          <strong>{intl.TOTAL}</strong>
        </p>

        <strong>
          <Currency>{addonSum}</Currency>
        </strong>
      </div>
    </>
  );
};

export const useAllVariations = () => {
  const weekObj = nextDeliveryWeekDateObj();
  const week = getWeek(weekObj);
  const year = getYear(weekObj);

  const { data: { productCategories } = {} } = useProductCategoriesQuery();

  const {
    data: { productsByCategories: standaloneProductsByCategories } = {},
  } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategories },
  );

  const allVariations =
    standaloneProductsByCategories?.flatMap(({ products }) =>
      products.flatMap((product) => product.variations),
    ) || [];

  return { allVariations };
};
